import React from 'react';
import { Link, useLocation } from 'react-router';
import { useTranslation } from 'react-i18next';
import { GridIcon } from '../../icons';
import { Mic, Puzzle, Users, Settings } from 'lucide-react';
import { LAYOUT } from '../layout';
import QuickRecordModal from '../../modules/session/ui/components/QuickRecordModal';
import { useState } from 'react';

const navItems = [
  {
    icon: <GridIcon className="w-5 h-5" />,
    name: "sidebar.home",
    path: "/",
    tourId: "sidebar-home",
  },
  {
    name: "sidebar.patients",
    icon: <Users size={20} />,
    path: "/patients",
    tourId: "sidebar-patients",
  },
  // Sessions & Therapeutic Plans removed from the menu — accessed via patient view.
  // Tools are not here either: they sit behind one button that opens a sheet,
  // because the catalog grows and this row cannot.
  {
    name: "sidebar.settings",
    icon: <Settings size={20} />,
    path: "/settings",
    tourId: "sidebar-settings",
  },
];

import { useSubscription } from '../../modules/subscription/application/useSubscription';
import { useAdoptedTools } from '../../modules/marketplace/application/useMarketplace';
import { useBreakpoint } from '../../hooks/useBreakpoint';
import { visibleToolNavItems } from '../../layout/toolNavItems';
import MobileToolsSheet from './MobileToolsSheet';

const BottomNavigationBar: React.FC = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const { isPremium } = useSubscription();
  const { isSmallScreen } = useBreakpoint();
  const { data: adoptedTools } = useAdoptedTools();
  const [showQuickRecord, setShowQuickRecord] = useState(false);
  const [showTools, setShowTools] = useState(false);

  const isActive = (path: string) => {
    if (path === "/") return location.pathname === "/";
    return location.pathname.startsWith(path);
  };

  // Same source as the desktop sidebar, so the two cannot drift into showing
  // a therapist different tools depending on which device they picked up.
  const toolItems = visibleToolNavItems(
    (adoptedTools ?? []).map((tool) => tool.slug)
  );

  // The tools button stands in for everything it opens, so it lights up on a
  // tool's own page too — otherwise navigating into Pagos would leave nothing
  // in the bar marked as where you are.
  const toolsActive =
    isActive("/tools-catalog") ||
    isActive("/reports") ||
    toolItems.some((tool) => isActive(`/${tool.path}`));

  // navItems = [Home, Patients, Settings], with Tools between Patients and
  // Settings. Five slots at most, whatever the therapist has adopted:
  // Plus: [LIVE], Home, Patients, Tools, Settings
  // Basic: Home, Patients, Tools, Settings
  const home = navItems.find((n) => n.name === "sidebar.home");
  const patients = navItems.find((n) => n.name === "sidebar.patients");
  const settings = navItems.find((n) => n.name === "sidebar.settings");

  const navLinks = [home, patients, "tools" as const, settings].filter(Boolean);

  // Recording leads the bar: it is the action a therapist opens the app to take,
  // and the first slot is the one the thumb reaches without crossing the row.
  const displayItems = isPremium
    ? [null, ...navLinks] // null = Record Live button
    : navLinks;

  return (
    <nav
      aria-label={t("sidebar.menu")}
      className={`fixed bottom-0 left-0 right-0 z-50 glass-effect border-t border-border-subtle transition-transform duration-[--duration-base] ease-[--ease-standard] ${isSmallScreen ? "translate-y-0" : "translate-y-full"}`}
      style={{ height: LAYOUT.NAV_HEIGHT.BOTTOM_BAR, paddingBottom: LAYOUT.SAFE_AREA_PADDING }}
    >
      <div className="flex items-center justify-around h-full max-w-2xl mx-auto px-2">
        {displayItems.map((nav) => {
          if (nav === null) {
            return (
              <button
                key="live-action"
                data-tour="mobile-record-live"
                onClick={() => setShowQuickRecord(true)}
                aria-label={t("sidebar.newLiveSession")}
                // Same shape and sizing as the navigation links beside it, so the
                // row stays one level of icons; the accent fill is what marks it
                // as the primary action rather than height or size.
                className="flex size-11 items-center justify-center rounded-lg bg-accent text-accent-contrast transition-colors duration-[--duration-fast] hover:bg-accent-hover active:scale-95"
              >
                <Mic aria-hidden="true" size={20} />
              </button>
            );
          }
          if (nav === "tools") {
            return (
              <button
                key="tools"
                type="button"
                data-tour="sidebar-tools-catalog"
                onClick={() => setShowTools(true)}
                aria-label={t("mobileTools.title")}
                aria-haspopup="dialog"
                aria-expanded={showTools}
                aria-current={toolsActive ? "page" : undefined}
                className={`flex size-11 items-center justify-center rounded-lg transition-colors duration-[--duration-fast]
                  ${toolsActive ? "bg-accent-tint text-accent-on-tint" : "text-content-secondary hover:bg-surface-sunken hover:text-content-primary"}`}
              >
                <Puzzle aria-hidden="true" size={20} />
              </button>
            );
          }
          if (!nav) return null; // defensive: skip any missing item
          return (
            <Link
              key={nav.name}
              to={nav.path}
              data-tour={nav.tourId}
              aria-label={t(nav.name)}
              aria-current={isActive(nav.path) ? "page" : undefined}
              className={`flex size-11 items-center justify-center rounded-lg transition-colors duration-[--duration-fast]
                ${isActive(nav.path) ? "bg-accent-tint text-accent-on-tint" : "text-content-secondary hover:bg-surface-sunken hover:text-content-primary"}`}
            >
              <span aria-hidden="true">{nav.icon}</span>
            </Link>
          );
        })}
      </div>
      {showQuickRecord && (
        <QuickRecordModal onClose={() => setShowQuickRecord(false)} />
      )}
      <MobileToolsSheet
        isOpen={showTools}
        onClose={() => setShowTools(false)}
        tools={toolItems}
      />
    </nav>
  );
};

export default BottomNavigationBar;
